<template>
  <div class="admin-manage-page">
    <header class="page-header">
      <div>
        <p class="eyebrow">ORDER & TRANSACTION</p>
        <h1>订单/交易管理</h1>
        <p>集中查看所有订单、平台补助、结算差额、平台手续费和服务方收入，后续可继续接入对账和申诉处理。</p>
      </div>
      <button class="primary-btn" @click="fetchOrders">刷新订单</button>
    </header>

    <section class="metric-grid">
      <article class="metric-card">
        <span>订单总数</span>
        <strong>{{ summary.totalOrders || 0 }}</strong>
        <p>当前筛选范围</p>
      </article>
      <article class="metric-card">
        <span>平台补助总额</span>
        <strong>{{ formatCoin(summary.totalPrepaid) }}</strong>
        <p>RoboCoin</p>
      </article>
      <article class="metric-card">
        <span>退款总额</span>
        <strong>{{ formatCoin(summary.totalRefund) }}</strong>
        <p>RoboCoin</p>
      </article>
      <article class="metric-card">
        <span>平台手续费</span>
        <strong>{{ formatCoin(summary.totalPlatformFee) }}</strong>
        <p>RoboCoin</p>
      </article>
    </section>

    <section class="filter-card">
      <el-input v-model.trim="filters.keyword" clearable placeholder="搜索订单ID / 任务 / 用户 / 机器人" @keyup.enter="fetchOrders"></el-input>
      <el-select v-model="filters.orderStatus" clearable placeholder="订单状态" @change="fetchOrders">
        <el-option label="已接单" value="ACCEPTED"></el-option>
        <el-option label="进行中" value="RUNNING"></el-option>
        <el-option label="已完成" value="FINISHED"></el-option>
        <el-option label="已取消" value="CANCELLED"></el-option>
        <el-option label="仲裁中" value="ARBITRATION"></el-option>
      </el-select>
      <el-select v-model="filters.paymentStatus" clearable placeholder="结算状态" @change="fetchOrders">
        <el-option label="未支付" value="UNPAID"></el-option>
        <el-option label="平台补助" value="PREPAID"></el-option>
        <el-option label="部分退款" value="PARTIAL_REFUND"></el-option>
        <el-option label="已结算" value="SETTLED"></el-option>
        <el-option label="已取消" value="CANCELLED"></el-option>
      </el-select>
      <label class="review-only-toggle">
        <el-switch v-model="filters.reviewOnly" @change="fetchOrders"></el-switch>
        <span>只看复核/仲裁</span>
      </label>
      <button class="primary-btn" @click="fetchOrders">查询</button>
    </section>

    <section class="table-card">
      <el-table v-loading="loading" :data="orders" @row-click="openDetail">
        <el-table-column prop="id" label="订单ID" width="90"></el-table-column>
        <el-table-column label="任务" min-width="190">
          <template #default="{ row }">
            <strong>{{ row.taskTitle || '未命名任务' }}</strong>
            <small>#{{ row.taskId }} · {{ serviceTypeLabel(row.serviceType) }}</small>
          </template>
        </el-table-column>
        <el-table-column label="双方用户" min-width="170">
          <template #default="{ row }">
            <span>发布：{{ row.publisherName || row.publisherUserId || '-' }}</span>
            <small>服务：{{ row.takerName || row.takerUserId || '-' }}</small>
          </template>
        </el-table-column>
        <el-table-column label="机器人" min-width="150">
          <template #default="{ row }">
            {{ row.robotName || '-' }}
            <small>{{ row.robotCategory || '' }}</small>
          </template>
        </el-table-column>
        <el-table-column label="支付/结算" min-width="210">
          <template #default="{ row }">
            <span>预付 {{ formatCoin(row.prepaidRobocoin) }}</span>
            <small>应付 {{ formatCoin(row.finalRobocoin) }} / 退 {{ formatCoin(row.settlementRefundRobocoin || row.refundRobocoin) }} / 补 {{ formatCoin(row.settlementExtraRobocoin) }}</small>
          </template>
        </el-table-column>
        <el-table-column label="手续费" width="130">
          <template #default="{ row }">{{ formatCoin(row.platformFeeRobocoin) }}</template>
        </el-table-column>
        <el-table-column label="状态" width="150">
          <template #default="{ row }">
            <el-tag :type="orderTag(row.orderStatus)">{{ orderStatusLabel(row.orderStatus) }}</el-tag>
            <small>{{ paymentStatusLabel(row.paymentStatus) }}</small>
          </template>
        </el-table-column>
        <el-table-column label="复核闭环" min-width="210">
          <template #default="{ row }">
            <span>{{ orderReviewTitle(row) }}</span>
            <small>{{ orderReviewDesc(row) }}</small>
          </template>
        </el-table-column>
        <el-table-column prop="updateTime" label="更新时间" min-width="170"></el-table-column>
        <el-table-column label="操作" width="170" fixed="right">
          <template #default="{ row }">
            <el-button size="mini" type="primary" @click.stop="openDetail(row)">详情</el-button>
            <el-button size="mini" @click.stop="openOrderLogs(row)">日志</el-button>
          </template>
        </el-table-column>
      </el-table>
    </section>

    <el-dialog title="订单交易详情" v-model="detailVisible" width="760px" append-to-body>
      <div v-if="current" class="detail-grid">
        <div><span>订单ID</span><strong>{{ current.id }}</strong></div>
        <div><span>任务ID</span><strong>{{ current.taskId }}</strong></div>
        <div><span>订单状态</span><strong>{{ orderStatusLabel(current.orderStatus) }}</strong></div>
        <div><span>结算状态</span><strong>{{ paymentStatusLabel(current.paymentStatus) }}</strong></div>
        <div><span>平台补助</span><strong>{{ formatCoin(current.prepaidRobocoin) }} RoboCoin</strong></div>
        <div><span>最终应付</span><strong>{{ formatCoin(current.finalRobocoin) }} RoboCoin</strong></div>
        <div><span>退款</span><strong>{{ formatCoin(current.settlementRefundRobocoin || current.refundRobocoin) }} RoboCoin</strong></div>
        <div><span>补扣</span><strong>{{ formatCoin(current.settlementExtraRobocoin) }} RoboCoin</strong></div>
        <div><span>平台手续费</span><strong>{{ formatCoin(current.platformFeeRobocoin) }} RoboCoin</strong></div>
        <div><span>服务方收入</span><strong>{{ formatCoin(current.providerIncomeRobocoin) }} RoboCoin</strong></div>
        <div><span>开始时间</span><strong>{{ current.serviceStartTime || '-' }}</strong></div>
        <div><span>结束时间</span><strong>{{ current.serviceEndTime || '-' }}</strong></div>
        <div><span>履约日志</span><strong>{{ current.fulfillmentLogCount || 0 }} 条</strong></div>
        <div><span>待复核证据</span><strong>{{ current.pendingReviewCount || 0 }} 条</strong></div>
        <div><span>最新复核</span><strong>{{ orderReviewTitle(current) }}</strong></div>
        <div><span>机器人释放</span><strong>{{ robotReleaseLabel(current.robotReleaseStatus) }}</strong></div>
      </div>
      <p v-if="current" class="detail-note">{{ current.settlementNote || current.derasNote || '暂无结算说明' }}</p>
      <div v-if="current" class="dialog-actions">
        <button class="primary-btn" type="button" @click="openOrderLogs(current)">查看履约日志</button>
      </div>
      <div v-if="current && (current.latestDecisionTitle || current.latestProofDigest || current.latestReviewTitle)" class="audit-card">
        <article>
          <span>最新裁决</span>
          <strong>{{ current.latestDecisionTitle || '暂无裁决' }}</strong>
          <p>{{ current.latestDecisionNote || current.latestReviewNote || '等待 DeRAS 复核或管理员裁决。' }}</p>
        </article>
        <article>
          <span>可信证据</span>
          <strong>{{ proofTypeLabel(current.latestProofType) }} · {{ proofStatusLabel(current.latestProofStatus) }}</strong>
          <p>{{ current.latestProofDigest || '暂无证据摘要' }}</p>
        </article>
      </div>
    </el-dialog>

    <el-dialog
      title="订单履约日志"
      v-model="logVisible"
      width="780px"
      custom-class="order-log-dialog"
      append-to-body
    >
      <div v-if="logOrder" class="order-log-panel" v-loading="logsLoading">
        <header>
          <p class="eyebrow">FULFILLMENT AUDIT</p>
          <h2>{{ logOrder.taskTitle || '订单履约日志' }}</h2>
          <span>订单 #{{ logOrder.id }} · {{ orderStatusLabel(logOrder.orderStatus) }} · {{ orderReviewTitle(logOrder) }}</span>
        </header>
        <div class="order-log-list">
          <article v-for="log in orderLogs" :key="log.id" :class="log.eventLevel">
            <i></i>
            <div>
              <span>{{ log.createTime || '-' }} · {{ eventTypeLabel(log.eventType) }} · {{ proofTypeLabel(log.proofType) }}</span>
              <strong>{{ log.eventTitle || '履约记录' }}</strong>
              <p>{{ log.eventContent || log.reviewNote || log.proofDigest || '-' }}</p>
              <em v-if="log.proofDigest">{{ log.proofDigest }}</em>
            </div>
          </article>
          <el-empty v-if="!logsLoading && orderLogs.length === 0" description="暂无履约日志"></el-empty>
        </div>
      </div>
    </el-dialog>
  </div>
</template>

<script>
import { getAdminOrderFulfillmentLogs, getAdminOrders } from '../api/market'

export default {
  name: 'AdminOrders',
  data() {
    return {
      loading: false,
      orders: [],
      summary: {},
      detailVisible: false,
      current: null,
      logVisible: false,
      logsLoading: false,
      logOrder: null,
      orderLogs: [],
      filters: {
        keyword: '',
        orderStatus: '',
        paymentStatus: '',
        reviewOnly: false
      }
    }
  },
  created() {
    this.fetchOrders()
  },
  methods: {
    async fetchOrders() {
      this.loading = true
      try {
        const response = await getAdminOrders({
          keyword: this.filters.keyword || undefined,
          orderStatus: this.filters.orderStatus || undefined,
          paymentStatus: this.filters.paymentStatus || undefined,
          reviewOnly: this.filters.reviewOnly ? 1 : undefined
        })
        if (response.data.code !== 200) {
          this.$message.error(response.data.msg || '订单加载失败')
          return
        }
        const data = response.data.data || {}
        this.orders = data.records || []
        this.summary = data.summary || {}
      } catch (error) {
        this.$message.error('订单请求失败')
      } finally {
        this.loading = false
      }
    },
    openDetail(row) {
      this.current = row
      this.detailVisible = true
    },
    async openOrderLogs(row) {
      if (!row || !row.id) return
      this.logOrder = row
      this.orderLogs = []
      this.logVisible = true
      this.logsLoading = true
      try {
        const response = await getAdminOrderFulfillmentLogs(row.id)
        if (response.data.code !== 200) {
          this.$message.error(response.data.msg || '履约日志加载失败')
          return
        }
        this.orderLogs = response.data.data || []
      } catch (error) {
        this.$message.error('履约日志请求失败')
      } finally {
        this.logsLoading = false
      }
    },
    formatCoin(value) {
      const numberValue = Number(value || 0)
      return Number.isFinite(numberValue) ? numberValue.toFixed(2) : '0.00'
    },
    serviceTypeLabel(value) {
      if (value === 'SHUTTLE') return '接驳服务'
      if (value === 'HOME_SERVICE') return '上门服务'
      return value || '未知服务'
    },
    orderStatusLabel(value) {
      const labels = {
        ACCEPTED: '已接单',
        RUNNING: '进行中',
        FINISHED: '已完成',
        CANCELLED: '已取消',
        ARBITRATION: '仲裁中'
      }
      return labels[value] || value || '未知'
    },
    paymentStatusLabel(value) {
      const labels = {
        UNPAID: '未支付',
        PREPAID: '平台补助',
        PAID: '平台补助',
        PARTIAL_REFUND: '部分退款',
        SETTLED: '已结算',
        CANCELLED: '已取消'
      }
      return labels[value] || value || '未记录'
    },
    orderTag(value) {
      if (value === 'FINISHED') return 'success'
      if (value === 'RUNNING') return 'warning'
      if (value === 'ARBITRATION') return 'danger'
      if (value === 'CANCELLED') return 'info'
      return ''
    },
    proofTypeLabel(value) {
      const labels = {
        TPM_SIGNATURE: 'TPM签名',
        GPS: 'GPS定位',
        USER_CONFIRM: '用户确认',
        DERAS_AUDIT: 'DeRAS审计',
        SETTLEMENT: '结算凭证',
        POT_OFFCHAIN: 'PoT链下证明'
      }
      return labels[value] || value || '未记录'
    },
    eventTypeLabel(value) {
      const labels = {
        WORKFLOW: '流程推进',
        ALERT_REVIEW: '异常复核',
        ARBITRATION: '人工仲裁',
        REVIEW_RESULT: '处理结果',
        ARBITRATION_RESULT: '仲裁结果',
        SDK_VERIFY: 'SDK验签',
        SDK_HEARTBEAT: 'SDK心跳',
        SETTLEMENT: '结算凭证',
        CANCEL: '取消订单',
        POT_PROOF: 'PoT证明'
      }
      return labels[value] || value || '履约记录'
    },
    proofStatusLabel(value) {
      const labels = {
        PENDING: '待处理',
        VERIFIED: '已验证',
        REVIEWING: '复核中',
        RESOLVED: '已处理',
        REJECTED: '已驳回',
        FAILED: '失败'
      }
      return labels[value] || value || '未记录'
    },
    reviewStatusLabel(value) {
      const labels = {
        PENDING: '待处理',
        PROCESSING: '处理中',
        RESOLVED: '已处理',
        REJECTED: '已驳回'
      }
      return labels[value] || value || '无复核'
    },
    robotReleaseLabel(value) {
      if (value === 'RELEASED') return '已释放/空闲'
      if (value === 'LOCKED') return '仍锁定任务'
      return '未同步'
    },
    orderReviewTitle(row) {
      if (!row) return '-'
      if (row.latestDecisionTitle) return row.latestDecisionTitle
      if (row.latestReviewTitle) return row.latestReviewTitle
      if (row.orderStatus === 'ARBITRATION') return '订单仲裁中'
      return '暂无复核'
    },
    orderReviewDesc(row) {
      if (!row) return ''
      if (row.latestDecisionNote) return row.latestDecisionNote
      if (row.latestReviewStatus) return `${this.reviewStatusLabel(row.latestReviewStatus)} · ${row.pendingReviewCount || 0} 条待复核`
      return `${row.fulfillmentLogCount || 0} 条履约日志`
    }
  }
}
</script>

<style scoped>
.admin-manage-page {
  min-height: 100vh;
  padding: 28px;
  color: #f6fbff;
}

.page-header,
.metric-card,
.filter-card,
.table-card {
  border: 1px solid rgba(143, 241, 199, 0.14);
  border-radius: 20px;
  background: rgba(255, 255, 255, 0.06);
}

.page-header {
  display: flex;
  justify-content: space-between;
  gap: 20px;
  padding: 24px;
  margin-bottom: 18px;
}

.eyebrow {
  margin: 0 0 8px;
  color: #8ff1c7;
  font-size: 12px;
  letter-spacing: 0.18em;
}

.page-header h1 {
  margin: 0;
  font-size: 30px;
}

.page-header p,
.metric-card p,
small {
  display: block;
  margin-top: 7px;
  color: rgba(246, 251, 255, 0.58);
}

.metric-grid {
  display: grid;
  grid-template-columns: repeat(4, minmax(0, 1fr));
  gap: 14px;
  margin-bottom: 18px;
}

.metric-card {
  padding: 18px;
}

.metric-card span {
  color: rgba(143, 241, 199, 0.84);
}

.metric-card strong {
  display: block;
  margin-top: 10px;
  font-size: 30px;
}

.filter-card {
  display: grid;
  grid-template-columns: minmax(240px, 1fr) 160px 160px 150px 110px;
  gap: 12px;
  padding: 16px;
  margin-bottom: 18px;
}

.review-only-toggle {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  min-height: 40px;
  padding: 0 12px;
  border-radius: 999px;
  color: rgba(246, 251, 255, 0.78);
  background: rgba(255, 255, 255, 0.06);
}

.table-card {
  padding: 16px;
}

.primary-btn {
  height: 40px;
  min-width: 108px;
  border: none;
  border-radius: 999px;
  color: #03101d;
  font-weight: 800;
  cursor: pointer;
  background: linear-gradient(135deg, #8ff1c7, #ffd08a);
}

.detail-grid {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 12px;
}

.detail-grid div {
  padding: 14px;
  border-radius: 14px;
  background: #f6f8fb;
}

.detail-grid span {
  display: block;
  color: #6b7280;
  font-size: 12px;
}

.detail-grid strong {
  display: block;
  margin-top: 6px;
  color: #101827;
}

.detail-note {
  margin: 14px 0 0;
  padding: 12px;
  border-radius: 12px;
  color: #344054;
  background: #eef3f8;
}

.audit-card {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 12px;
  margin-top: 14px;
}

.audit-card article {
  padding: 14px;
  border-radius: 14px;
  border: 1px solid rgba(143, 241, 199, 0.18);
  background: #f6f8fb;
}

.audit-card span {
  display: block;
  margin-bottom: 8px;
  color: #0f766e;
  font-size: 12px;
  font-weight: 900;
}

.audit-card strong {
  color: #101827;
}

.audit-card p {
  margin: 8px 0 0;
  color: #475467;
  line-height: 1.65;
  word-break: break-all;
}

.dialog-actions {
  display: flex;
  justify-content: flex-end;
  margin-top: 14px;
}

.order-log-dialog :deep(.el-dialog) {
  border-radius: 24px;
  overflow: hidden;
  background:
    radial-gradient(circle at 12% 0%, rgba(143, 241, 199, 0.16), transparent 28%),
    linear-gradient(135deg, #06111e, #081827);
}

.order-log-dialog :deep(.el-dialog__header) {
  border-bottom: 1px solid rgba(255, 255, 255, 0.08);
  background: rgba(255, 255, 255, 0.04);
}

.order-log-dialog :deep(.el-dialog__title),
.order-log-dialog :deep(.el-dialog__close),
.order-log-panel {
  color: #f6fbff;
}

.order-log-panel header {
  margin-bottom: 16px;
}

.order-log-panel h2 {
  margin: 0 0 8px;
  font-size: 28px;
}

.order-log-panel header span {
  color: rgba(246, 251, 255, 0.62);
}

.order-log-list {
  display: grid;
  gap: 10px;
  max-height: 540px;
  overflow: auto;
}

.order-log-list article {
  display: grid;
  grid-template-columns: 14px minmax(0, 1fr);
  gap: 10px;
  padding: 13px;
  border-radius: 16px;
  border: 1px solid rgba(255, 255, 255, 0.08);
  background: rgba(255, 255, 255, 0.055);
}

.order-log-list article > i {
  width: 10px;
  height: 10px;
  margin-top: 5px;
  border-radius: 50%;
  background: #8ff1c7;
}

.order-log-list article.WARNING > i {
  background: #ffd08a;
}

.order-log-list article.DANGER > i {
  background: #ff6b6b;
}

.order-log-list span,
.order-log-list em {
  display: block;
  color: rgba(246, 251, 255, 0.52);
  font-size: 12px;
  font-style: normal;
}

.order-log-list strong {
  display: block;
  margin-top: 5px;
  color: #fff;
}

.order-log-list p {
  margin: 7px 0 0;
  color: rgba(246, 251, 255, 0.66);
  line-height: 1.65;
}

.order-log-list em {
  margin-top: 7px;
  word-break: break-all;
  font-family: ui-monospace, monospace;
}

@media (max-width: 980px) {
  .metric-grid,
  .filter-card {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }
}

@media (max-width: 640px) {
  .admin-manage-page {
    padding: 18px;
  }

  .page-header,
  .metric-grid,
  .filter-card,
  .detail-grid,
  .audit-card {
    grid-template-columns: 1fr;
    flex-direction: column;
  }
}
</style>
